export const middlewareGateway = async <T>(url: string, opts?: RequestInit) => {
  try {
    const { headers: extraHeaders, ...rest } = opts || {}

    // Client side middleware url
    const csrMiddlewareUrl =
      useRuntimeConfig().public.MIDDLEWARE_CSR_URL ||
      `http://localhost:${useRuntimeConfig().public.MIDDLEWARE_PORT || 8181}`

    // Server side middleware url
    const middlewareUrl =
      useRuntimeConfig().public.MIDDLEWARE_API_URL ||
      `http://localhost:${useRuntimeConfig().public.MIDDLEWARE_PORT || 8181}`

    const baseURL = import.meta.server ? middlewareUrl : csrMiddlewareUrl

    const fetchUrl = `${baseURL}${url}`

    // Middleware requests may run without a usable shopper token (for example, on a first visit or
    // while an access token is being refreshed). Continue without auth so the downstream endpoint can
    // decide whether authentication is required. SFCC SDK SSR calls are handled separately by the
    // server auth middleware and do not depend on this fallback.
    let headers:
      | {
          Authorization: string
        }
      | undefined = undefined
    try {
      headers = await useShopper().getAuthHeaders()
    } catch {
      // Missing/expired token is an expected lifecycle state (cold start, refresh in flight, bot without session).
      // Proceed without auth headers; downstream response.ok / content-type checks will surface real failures.
    }

    const response = await fetch(fetchUrl, {
      headers: {
        ...headers,
        ...extraHeaders,
      },
      ...rest,
    })

    if (!response.ok) {
      throw new Error(response.statusText)
    }

    // Detect an upstream proxy / SFCC edge serving an HTML error or challenge page with 200 OK
    // (typically bot traffic without a valid token) by the body, not the content-type header:
    // NestJS serialises primitive returns (booleans/strings) as text/html, so a content-type
    // check falsely rejects legitimate JSON primitives.
    const text = await response.text()
    if (text.trimStart().startsWith('<')) {
      throw new Error(`middlewareGateway: expected JSON from ${url}, got an HTML page`)
    }

    if (!text) return undefined as T

    try {
      return JSON.parse(text) as T
    } catch {
      throw new Error(`middlewareGateway: invalid JSON from ${url}`)
    }
  } catch (e) {
    if (e instanceof Error) throw e
  }
}
